import {
  LandingPageNumber,
  VehiclePageNumber,
  OptionsPageNumber,
  AftermarketPageNumber,
  HistoryPageNumber,
  ImageCapturePageNumber,
  ExteriorPageNumber,
  GlassPageNumber,
  InteriorPageNumber,
  MechanicalPageNumber,
  ContactPageNumber,
  SummaryPageNumber
} from '../../constants'
import { BrowserUtils } from '../../utils/siteUtils'

const STEPNAMES = (hidePage = []) => [
  { name: 'Basic Info', shortName: 'basicinfo', pageNumbers: [LandingPageNumber] },
  { name: 'Details', shortName: 'vehdetails', pageNumbers: [VehiclePageNumber, OptionsPageNumber] },
  { name: 'Modifications', shortName: 'aftermkt', pageNumbers: hidePage.includes('vehicleModifications') ? [] : [AftermarketPageNumber] },
  { name: 'History', shortName: 'vehhist', pageNumbers: hidePage.includes('vehicleHistory') ? [] : [HistoryPageNumber] },
  {
    name: 'Condition',
    shortName: 'vehcond',
    pageNumbers: [ImageCapturePageNumber, ExteriorPageNumber, GlassPageNumber, InteriorPageNumber, MechanicalPageNumber]
  },
  { name: 'Your Info', shortName: 'contact', pageNumbers: [ContactPageNumber] },
  { name: 'Get Offer', shortName: 'cashoffer', editName: 'Review Offer', pageNumbers: [SummaryPageNumber] }
]
const convertStepToIndex = (steps, step) => {
  if (step > SummaryPageNumber) step = SummaryPageNumber

  let index = steps.findIndex(x => x.pageNumbers.includes(step))
  if (index === steps.length - 1 && step !== SummaryPageNumber) {
    index = steps.length - 2
  }
  return index
}

const convertIndexToStep = (steps, index, datEdit = false, autoCheckData, vinResults, urlState, experienceState, updateGlobalState, nextStep = null) => {
  let step = steps.slice(0, index).reduce((acc, cur) => (acc += cur.pageNumbers.length), 0)
  if (nextStep?.pageNumbers?.length > 0) {
    step = nextStep.pageNumbers[0]
  }
  let autoCheckAndVehicleActivity = autoCheckData && vinResults && !vinResults.vehicleActivityApplicationDown
  const ymmt = urlState && urlState.displayYMM && !urlState.vin
  // based on slice logic, step still gets set to 4 and errors, this forces to 5
  if (experienceState && !experienceState.isAdmin && !ymmt) {
    if (autoCheckAndVehicleActivity && step === HistoryPageNumber) {
      step = ImageCapturePageNumber
    }
  }

  if (experienceState && (experienceState.dealerIsDirectToKBBv2 || experienceState.skipPhotoUpload) && step === ImageCapturePageNumber) {
    // Default Condition Page values for analytics. This normally happens in ImageCapture.js, but since we're skipping it...
    updateGlobalState({ np: ExteriorPageNumber, cp: ExteriorPageNumber })
    step = ExteriorPageNumber
  }

  if (datEdit && step === ImageCapturePageNumber) step++

  return step
}

export default props => {
  const {
    urlState,
    year = {},
    make = {},
    model = {},
    nextDisabled,
    errorFunc,
    updateGlobalState,
    autoCheckData,
    vinResults,
    experienceState,
    experienceState: { isOfferCreationComponentEnabled, isOfferCreationComponentDatEnabled, isAdmin }
  } = props
  const datEdit = urlState && !!urlState.edit
  const ymmt = urlState && urlState.displayYMM && !urlState.vin
  let steps = STEPNAMES([])
  const autoCheckAndVehicleActivity = autoCheckData && vinResults && !vinResults.vehicleActivityApplicationDown
  const isExperienceStateAdmin = experienceState && isAdmin
  const isResubmit = urlState && urlState.pa.length > 0 && urlState.pa.toString().includes('offer')

  if (!isExperienceStateAdmin) {
    if (!ymmt && autoCheckAndVehicleActivity && !isOfferCreationComponentEnabled) {
      // if not edit offer
      steps = STEPNAMES(['vehicleHistory'])
    } else if (isResubmit && autoCheckAndVehicleActivity) {
      steps = STEPNAMES(['vehicleHistory'])
    } else if (!ymmt && isOfferCreationComponentEnabled && autoCheckAndVehicleActivity) {
      //if not edit offer and not YMMT and offer creation flag is on and not resubmit
      steps = STEPNAMES(['vehicleHistory', 'vehicleModifications'])
    } else if (ymmt && isOfferCreationComponentEnabled) {
      //if not edit offer and is YMMT and offer creation is on
      steps = STEPNAMES(['vehicleModifications'])
    } else if (isOfferCreationComponentEnabled && !autoCheckAndVehicleActivity && !ymmt && !isResubmit) {
      //if not edit offer and is not YMMT and autocheck is down
      steps = STEPNAMES(['vehicleModifications'])
    }
  } else if (isOfferCreationComponentDatEnabled) {
    steps = STEPNAMES(['vehicleModifications'])
  }

  let currentStep = typeof urlState !== 'undefined' && urlState.p >= LandingPageNumber ? convertStepToIndex(steps, urlState.p) : convertStepToIndex(steps, VehiclePageNumber)
  let furthestStep = typeof urlState !== 'undefined' && convertStepToIndex(steps, urlState.lp || urlState.p)
  const showProgressBar = currentStep >= LandingPageNumber

  const stepClick = (step, index) => {
    // check for error state otherwise navigate
    if (index <= furthestStep && (!nextDisabled || !urlState.lp || urlState.p === urlState.lp)) {
      updateGlobalState({ fireAnalytics: `icoheader_statusbar_${step.shortName}`, lp: urlState.lp })
      BrowserUtils.navigateWithUrlState({ year, make, model, urlState, updateState: { p: convertIndexToStep(steps, index, datEdit, autoCheckData, vinResults, urlState, experienceState, updateGlobalState, step), lp: urlState.lp } })
    } else errorFunc && errorFunc()
  }

  const getStepName = step => {
    return datEdit ? step.editName || step.name : step.name
  }

  ////// **** - Integrated Breadcrumb Code - **** //////
  const removedPages = steps
    .map((step, index) => {
      return { ...step, index }
    })
    .filter(step => step.pageNumbers.length === 0)

  let currentPage = currentStep
  let furthestPage = furthestStep
  if (currentStep && removedPages.length >= 0 && removedPages.filter(step => currentStep >= step.index).length > 0) {
    currentPage = currentStep - removedPages.length
  }
  if (furthestStep && removedPages.length >= 0 && removedPages.filter(step => furthestStep >= step.index).length > 0) {
    furthestPage = furthestStep - removedPages.length
  }

  let pages = steps
    .filter(step => step?.pageNumbers?.length > 0)
    .map((step, index) => {
      return {
        name: step.shortName,
        title: step.name,
        isValidated: index < currentPage || index < furthestPage,
        pageNumbers: step.pageNumbers,
        pageNumber: index,
        furthestPage: furthestPage,
        currentPage: currentPage
      }
    })
  ////// **** - Integrated Breadcrumb Code - **** //////
  return {
    getStepName,
    showProgressBar,
    stepClick,
    steps,
    currentStep,
    furthestStep,
    currentPage,
    furthestPage,
    pages
  }
}
